import { useCallback, useRef, useState } from "react";
import {
  searchEmployees,
  searchOrderReferences,
  searchOrders,
} from "@/lib/projectOverview";
import { formatAuftragNumberWithYear } from "@/lib/orderDraftUtils";
import { searchTodoReferences } from "@/lib/todos";
import type { NoteComposerContext, NoteSuggestionItem } from "@/projectOverview/notes/types";

export function useNoteSuggestions(
  orderId: number,
  options?: {
    /** Globale Suche ohne Auftragskontext (To-Dos): + Bestellung, * Nachweis, § Dokument. */
    globalSearch?: boolean;
  },
) {
  const globalSearch = !!options?.globalSearch;
  const [items, setItems] = useState<NoteSuggestionItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [anchor, setAnchor] = useState<NoteComposerContext | null>(null);
  const [loading, setLoading] = useState(false);
  const seqRef = useRef(0);

  const close = useCallback(() => {
    setItems([]);
    setActiveIndex(0);
    setAnchor(null);
    setLoading(false);
  }, []);

  const load = useCallback(
    async (ctx: NoteComposerContext | null) => {
      if (!ctx) {
        close();
        return;
      }
      setAnchor(ctx);
      setLoading(true);
      const requestId = ++seqRef.current;
      try {
        let next: NoteSuggestionItem[] = [];
        if (ctx.trigger === "@") {
          const rows = await searchEmployees(ctx.query, 50);
          next = (Array.isArray(rows) ? rows : []).map((row) => ({
            id: String(row.id ?? row.email ?? row.name ?? ""),
            label: String(row.label ?? row.name ?? ""),
            subtitle: row.subtitle ?? row.email ?? undefined,
            refType: "employee",
          }));
        } else if (ctx.trigger === "#") {
          const rows = await searchOrders(ctx.query || "#", 8);
          next = (Array.isArray(rows) ? rows : []).map((row) => ({
            id: String(row.id),
            label: formatAuftragNumberWithYear(row),
            subtitle: [row.kunde, row.beschreibung].filter(Boolean).join(" · ") || undefined,
            refType: "auftrag",
          }));
        } else if (ctx.trigger === "+" && orderId > 0) {
          const rows = await searchOrderReferences(orderId, "orders", ctx.query, 8);
          next = (Array.isArray(rows) ? rows : []).map((row) => ({
            id: String(row.id),
            label: String(row.label || row.display || row.id),
            subtitle: row.subtitle ? String(row.subtitle) : undefined,
            refType: "order",
          }));
        } else if (ctx.trigger === "+" && globalSearch) {
          const rows = await searchTodoReferences("orders", ctx.query, 8);
          next = (Array.isArray(rows) ? rows : []).map((row) => ({
            id: String(row.id),
            label: String(row.label || row.id),
            subtitle: row.subtitle ? String(row.subtitle) : undefined,
            refType: "order",
          }));
        } else if (ctx.trigger === "*" && orderId > 0) {
          const rows = await searchOrderReferences(orderId, "timesheets", ctx.query, 8);
          next = (Array.isArray(rows) ? rows : []).map((row) => ({
            id: String(row.id),
            label: String(row.label || row.display || row.id),
            subtitle: row.subtitle ? String(row.subtitle) : undefined,
            refType: "timesheet",
          }));
        } else if (ctx.trigger === "*" && globalSearch) {
          const rows = await searchTodoReferences("timesheets", ctx.query, 8);
          next = (Array.isArray(rows) ? rows : []).map((row) => ({
            id: String(row.id),
            label: String(row.label || row.id),
            subtitle: row.subtitle ? String(row.subtitle) : undefined,
            refType: "timesheet",
          }));
        } else if (ctx.trigger === "%" && globalSearch) {
          const rows = await searchTodoReferences("material", ctx.query, 8);
          next = (Array.isArray(rows) ? rows : []).map((row) => ({
            id: String(row.id),
            label: String(row.label || row.id),
            subtitle: row.subtitle ? String(row.subtitle) : undefined,
            refType: "material_anforderung",
          }));
        } else if (ctx.trigger === "§" && globalSearch) {
          const rows = await searchTodoReferences("dokumente", ctx.query, 8);
          next = (Array.isArray(rows) ? rows : []).map((row) => ({
            id: String(row.id),
            label: String(row.label || row.id),
            subtitle: row.subtitle ? String(row.subtitle) : undefined,
            refType: "geschaeftsdokument",
          }));
        }
        if (requestId !== seqRef.current) return;
        setItems(
          next.filter((item) => item.id && item.label),
        );
        setActiveIndex(0);
      } catch {
        if (requestId === seqRef.current) {
          setItems([]);
        }
      } finally {
        if (requestId === seqRef.current) {
          setLoading(false);
        }
      }
    },
    [close, orderId, globalSearch],
  );

  return {
    items,
    activeIndex,
    setActiveIndex,
    anchor,
    setAnchor,
    load,
    close,
    loading,
    open: !!anchor,
  };
}
